import React, { useMemo, useState } from "react";
import { Button, Group, Tooltip } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { useAuthUser } from "react-auth-kit";
import { useServerApi } from "../hooks/userServerApi";
import { openFileUrl } from "../utils/openReport";
import { useInvestmentTransactionSettings } from "../contexts/investmentTransactionSettingsContext";
import { useHideFormActionList } from "../hooks/useHideFormActionList";
import {
  createArFormValidators,
  getArFormPrintDisabledReason,
  isArFormOpsRole,
} from "../helpers/arFormUtils";

const OrderInvestmentTransactionActionList = ({ form }) => {
  const hideActionList = useHideFormActionList(form);
  const auth = useAuthUser();
  const isOps = isArFormOpsRole(auth()?.userRole?.name);
  const [api] = useServerApi();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { nonWrapUpfrontFeeRules } = useInvestmentTransactionSettings();
  const transactionId = form?.values?._id;

  const validators = useMemo(
    () => createArFormValidators(nonWrapUpfrontFeeRules),
    [nonWrapUpfrontFeeRules]
  );

  const disabledReason = useMemo(
    () =>
      getArFormPrintDisabledReason({
        transactionId,
        values: form?.values,
        validators,
      }),
    [transactionId, form?.values, validators]
  );

  if (hideActionList || !isOps) return null;

  const handlePrint = async () => {
    if (disabledReason) return;

    try {
      setLoading(true);
      const result = await api.OrderInvestmentTransaction.printArForm(
        transactionId,
        form.values.arForm
      );
      if (result?.url) {
        await openFileUrl(result.url);
        return;
      }
      throw new Error("No print URL returned");
    } catch (error) {
      showNotification({
        color: "red",
        title: t("Error"),
        message: error?.message || "Failed to print AR Form",
      });
    } finally {
      setLoading(false);
    }
  };

  const button = (
    <Button
      fullWidth
      onClick={handlePrint}
      loading={loading}
      disabled={!!disabledReason || loading}
    >
      {loading ? t("Printing") : t("Print AR Form")}
    </Button>
  );

  return (
    <Group mt={10}>
      {disabledReason ? (
        <Tooltip
          label={disabledReason}
          multiline
          maw={320}
          withArrow
          withinPortal
        >
          <span style={{ display: "block", width: "100%" }}>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
    </Group>
  );
};

export default OrderInvestmentTransactionActionList;
